import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { ShieldCheckIcon } from '@heroicons/react/24/outline';
import api, { getErrorMessage } from '../../services/api';

/**
 * Qué puede hacer el gestor de la empresa (06-reparto §4.5).
 *
 * La empresa recorta, no amplía: la lista es lo que el gestor puede hacer hoy,
 * y todo empieza permitido. El servidor decide (`policies/`); esto solo lo
 * configura. Ver docs/ar-permisos-gestor-plan.md.
 *
 * `companyId` es la empresa del perfil que se está viendo: así el admin lo
 * puede tocar por un cliente. A la empresa, el servidor le impone la suya.
 */
const GestorPermissions = ({ companyId, isAdmin = false }) => {
  const [datos, setDatos] = useState(null);
  const [guardando, setGuardando] = useState(false);
  const params = isAdmin ? { company_id: companyId } : {};

  const cargar = useCallback(async () => {
    try {
      const { data } = await api.get('/team-permissions', { params: isAdmin ? { company_id: companyId } : {} });
      setDatos(data.data);
    } catch (err) {
      toast.error(getErrorMessage(err));
    }
  }, [companyId, isAdmin]);

  useEffect(() => {
    cargar();
  }, [cargar]);

  const alternar = async capability => {
    const nuevo = !datos.gestor[capability];
    setGuardando(true);
    try {
      const { data } = await api.put('/team-permissions', { ...params, gestor: { ...datos.gestor, [capability]: nuevo } });
      setDatos(data.data);
      toast.success(nuevo ? 'El gestor ya puede hacerlo' : 'El gestor ya no puede hacerlo');
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setGuardando(false);
    }
  };

  if (!datos) return null;

  return (
    <div className='bg-white rounded-lg border border-slate-200 p-6'>
      <div className='flex items-center mb-1'>
        <ShieldCheckIcon className='h-5 w-5 text-blue-600 mr-2' />
        <h3 className='text-lg font-medium text-slate-900'>Qué puede hacer el gestor</h3>
      </div>
      <p className='text-sm text-slate-500 mb-4'>
        Por defecto puede todo esto. Quítale lo que no quieras que haga; vale para todos tus gestores.
      </p>
      <div className='divide-y divide-slate-100'>
        {datos.capabilities.map(c => (
          <label key={c.value} className='flex items-center justify-between gap-3 py-2 text-sm text-slate-700'>
            {c.label}
            <input
              type='checkbox'
              checked={datos.gestor[c.value] !== false}
              disabled={guardando || !datos.can_edit}
              onChange={() => alternar(c.value)}
              aria-label={c.label}
            />
          </label>
        ))}
      </div>
    </div>
  );
};

export default GestorPermissions;
